import { FlexColumn, FlexRow, LabeledInput, StyledButton, useLoading, useModal } from '@gorila-shared-ui/components';
import { HeadingSmall, ParagraphMedium } from 'baseui/typography';
import { useEffect } from 'react';
import { areEqual } from 'react-window';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { deleteGroup, postGroup, updateGroup } from '../../services/groupService';
import { apiParamsState } from '../../storage/app';
import { editingGroupState, groupState, selectedGroupIdState, updateGroupsState } from '../../storage/groups';
import { Group } from '../../types/group';
import { AssetListPicker } from '../shared/assetListPicker/AssetListPicker';
import ConfirmModal from '../shared/ConfirmModal';

export function GroupDetail() {
  const { theme, css } = useStyles();
  const { openModal, closeModal, showModal } = useModal();
  const { loading, startLoading, stopLoading } = useLoading();
  const [editingGroup, setEditingGroup] = useRecoilState(editingGroupState);
  const resetEditingGroup = useResetRecoilState(editingGroupState);
  const apiParams = useRecoilValue(apiParamsState);
  const [selectedGroupId, setSelectedGroupId] = useRecoilState(selectedGroupIdState);
  const group = useRecoilValue(groupState(selectedGroupId || ''));
  const resetSelectedGroupId = useResetRecoilState(selectedGroupIdState);
  const setUpdateGroups = useSetRecoilState(updateGroupsState);
  const isNewGroup = editingGroup?._id === '';
  const { showFailFeedback, showDeletionFeedback, showPositiveFeedback } = useFeedback();

  useEffect(() => {
    resetEditingGroup();
    return () => {
      resetEditingGroup();
    };
  }, [selectedGroupId]);

  if (editingGroup === undefined) return null;

  const canSubmit = !!editingGroup.name && !!editingGroup.assignedAssets.length;
  const hasUpdates = !areEqual(group || {}, editingGroup);

  const onDeleteGroup = async () => {
    if (!apiParams || selectedGroupId === undefined) return;
    const { error } = await deleteGroup(apiParams.clientId, apiParams.subClientId, selectedGroupId);
    if (!error) {
      showDeletionFeedback(FEEDBACK.deleted(FEEDBACK_PREFIXES.group, group?.name));
      setUpdateGroups('update');
      resetSelectedGroupId();
      closeModal();
    } else {
      showFailFeedback(error || FEEDBACK.failedDeletion(FEEDBACK_PREFIXES.group, group?.name));
    }
  };

  const onInputChange = (value: string | boolean | number | null, field: keyof Group) => {
    setEditingGroup((prev) => ({ ...prev!, [field]: value }));
  };

  const onSaveGroup = async () => {
    startLoading();
    const { id, error } = isNewGroup ? await postGroup(editingGroup) : await updateGroup(editingGroup);
    if (!error && id) {
      if (isNewGroup) setSelectedGroupId(id);
      showPositiveFeedback(
        isNewGroup
          ? FEEDBACK.created(FEEDBACK_PREFIXES.group)
          : FEEDBACK.edited(FEEDBACK_PREFIXES.group, editingGroup.name)
      );
      setUpdateGroups(id);
    } else {
      showFailFeedback(
        error || isNewGroup
          ? FEEDBACK.failedCreation(FEEDBACK_PREFIXES.group)
          : FEEDBACK.failedEdition(FEEDBACK_PREFIXES.group, group?.name)
      );
    }
    stopLoading();
  };

  const onAssetsSelectionChange = (assetIds: string[]) => {
    setEditingGroup((prev) => ({ ...prev!, assignedAssets: assetIds }));
  };

  return (
    <FlexColumn
      classNames={css({
        flex: 1,
        [theme.mediaQuery.large]: {
          maxWidth: '60%',
        },
        [theme.mediaQuery.extraLarge]: {
          maxWidth: '50%',
        },
      })}
    >
      <HeadingSmall margin={0}>{isNewGroup ? 'Nuevo grupo' : group?.name}</HeadingSmall>
      <FlexColumn classNames={css({ flex: 1 })}>
        <LabeledInput
          label="Nombre"
          value={editingGroup.name}
          onChange={(value) => onInputChange(value, 'name')}
          required
        />
        <AssetListPicker
          assignedAssetIds={editingGroup.assignedAssets}
          onAssignedChange={onAssetsSelectionChange}
          required
        />
      </FlexColumn>
      <FlexRow
        classNames={`${css({ alignItems: 'center', justifyContent: isNewGroup ? 'flex-end' : 'space-between' })}`}
      >
        {!isNewGroup && (
          <StyledButton
            kind="tertiary"
            onClick={openModal}
          >
            Eliminar
          </StyledButton>
        )}
        <FlexRow gap={theme.sizing.scale300}>
          <StyledButton
            kind="tertiary"
            onClick={resetSelectedGroupId}
          >
            Cancelar
          </StyledButton>
          <StyledButton
            onClick={onSaveGroup}
            isLoading={loading}
            disabled={!canSubmit || !hasUpdates}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
      <ConfirmModal
        isOpen={showModal}
        onCancel={closeModal}
        onConfirm={onDeleteGroup}
        confirmText="Eliminar"
        description={<ParagraphMedium>¿Desea eliminar "{group?.name}"?</ParagraphMedium>}
      />
    </FlexColumn>
  );
}
